import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import StudentLayout from "@/components/StudentLayout";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Megaphone, Loader2, Download, FileText, Star, X } from "lucide-react";
import AnnouncementPamphlet from "@/components/announcement/AnnouncementPamphlet";
import { exportImage, exportPdf } from "@/components/announcement/exportUtils";
import { formatDate } from "@/components/announcement/templates";

export default function StudentAnnouncements() {
  const [notices, setNotices] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/student/notices"); setNotices(data.notices); }
    catch { toast.error("Could not load announcements"); }
  };
  useEffect(() => { load(); }, []);

  const openNotice = async (n) => {
    setOpen(n);
    if (!n.is_read) {
      api.post(`/student/notices/${n.id}/read`).catch(() => {});
      setNotices((arr) => arr.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
    }
  };

  return (
    <StudentLayout>
      <div className="max-w-3xl mx-auto space-y-5" data-testid="student-announcements">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Communication</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Announcements</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Notices and circulars from your school.</p>
        </div>

        {notices === null ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : notices.length === 0 ? (
          <div className="py-16 text-center rounded-2xl border border-dashed border-slate-200 dark:border-slate-700" data-testid="student-notices-empty">
            <Megaphone className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <div className="text-slate-900 dark:text-slate-100 font-medium">No announcements yet</div>
          </div>
        ) : (
          <div className="grid gap-3">
            {notices.map((n) => (
              <button key={n.id} onClick={() => openNotice(n)} data-testid={`student-notice-${n.id}`}
                className="text-left rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:border-sky-300 transition-colors flex items-start gap-3">
                {n.brand_snapshot?.logo && <img src={n.brand_snapshot.logo} alt="" className="h-11 w-11 rounded-lg object-contain shrink-0 bg-slate-50 dark:bg-slate-800" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!n.is_read && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500 text-white" data-testid={`new-badge-${n.id}`}>NEW</span>}
                    {n.priority === "important" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500 text-white inline-flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> IMPORTANT</span>}
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300">{n.category}</span>
                  </div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100 mt-1.5 truncate">{n.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{formatDate(n.published_at || n.announcement_date)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {open && <NoticeModal notice={open} onClose={() => setOpen(null)} />}
    </StudentLayout>
  );
}

function NoticeModal({ notice, onClose }) {
  const ref = useRef(null);
  const [exporting, setExporting] = useState(false);
  const brand = notice.brand_snapshot || {};

  const doExport = async (kind) => {
    if (!ref.current) return;
    setExporting(true);
    try {
      if (kind === "pdf") await exportPdf(ref.current, notice.title, "portrait");
      else await exportImage(ref.current, notice.title, kind);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="student-notice-modal">
        <div className="sticky top-0 z-10 flex items-center justify-end gap-1.5 p-2 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-100 dark:border-slate-800">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("pdf")} disabled={exporting} data-testid="student-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("png")} disabled={exporting} data-testid="student-export-png"><Download className="h-4 w-4 mr-1" /> Image</Button>
          {exporting && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="close-notice"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 bg-slate-100 dark:bg-slate-800/50">
          <div className="shadow-xl">
            <AnnouncementPamphlet ref={ref} data={notice} profile={brand} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
